<script lang="ts" setup>
import AwakeningMedal from "@/components/AwakeningMedal.vue";
import BaseCard from "@/components/BaseCard.vue";
import BaseContent from "@/components/BaseContent.vue";
import BasePageHeading from "@/components/BasePageHeading.vue";
import DokkanCharacterList from "@/components/DokkanCharacterList.vue";
import TheLayout from "@/components/TheLayout.vue";
import {
  useGetAreasFromStages,
  useGetQuestsFromStagesByArea,
} from "@/composables/stage";
import { Link, usePage } from "@inertiajs/vue3";
import _ from "lodash";
import { computed } from "vue";

const page = usePage();
const props = defineProps<{
  awakening_medal: AwakeningMedal;
}>();

const acquiredFromStage = computed(() => {
  return (
    props.awakening_medal.stages !== null &&
    props.awakening_medal.stages.length > 0
  );
});

const acquiredFromBabaShop = computed(() => {
  return (
    props.awakening_medal.baba_shop_sales !== null &&
    props.awakening_medal.baba_shop_sales.length > 0
  );
});

const acquiredFromZBattle = computed(() => {
  return props.awakening_medal.z_battle !== null;
});

const acquiredFromWorldTournament = computed(() => {
  return (
    props.awakening_medal.world_tournaments !== null &&
    props.awakening_medal.world_tournaments.length > 0
  );
});

const areas = useGetAreasFromStages(props.awakening_medal.stages);

const consumersSorted = computed(() => {
  return _.uniqBy(
    _.orderBy(
      props.awakening_medal.awakenings_awakening_items
        .map((a) => a.awakenings)
        .flat()
        .map((a) => a.character),
      [(c) => c.rarity, (c) => c.variant_id],
      ["desc"],
    ),
    "id",
  );
});
</script>

<template>
  <TheLayout
    :description="`Details of, acquirement methods for and a list of characters that consume the ${awakening_medal.name} Awakening Medal in DRAGON BALL Z Dokkan Battle.`"
    :title="awakening_medal.name"
    heading="Awakening Medals"
  >
    <BasePageHeading>
      {{ awakening_medal.name }}
    </BasePageHeading>

    <div class="mx-auto flex w-full flex-col gap-4">
      <div class="flex w-full flex-col gap-4 lg:flex-row">
        <BaseCard class="sm:!flex-row lg:!flex-col xl:!flex-row">
          <template #heading> Details</template>

          <AwakeningMedal
            :awakening-medal="awakening_medal"
            :clickable="false"
            class="grow-0 place-self-center sm:place-self-start lg:place-self-center xl:place-self-start"
          />
          <div class="flex flex-col gap-4">
            <BaseContent header="Description">
              {{ awakening_medal.description }}
            </BaseContent>

            <BaseContent
              v-if="
                awakening_medal.zeni > 0 && awakening_medal.trade_points > 0
              "
              header="Sells for"
            >
              <div class="flex flex-col gap-1">
                <ul class="ml-4 list-disc">
                  <li v-if="awakening_medal.zeni > 0">
                    <span class="font-medium text-green-100">{{
                      awakening_medal.zeni.toLocaleString()
                    }}</span>
                    Zeni
                  </li>
                  <li v-if="awakening_medal.trade_points > 0">
                    <span class="font-medium text-green-100">{{
                      awakening_medal.trade_points.toLocaleString()
                    }}</span>
                    Trade Points
                  </li>
                </ul>
              </div>
            </BaseContent>

            <BaseContent header="Acquired from">
              <ul class="ml-4 list-disc">
                <li v-if="acquiredFromStage">Stages</li>
                <li v-if="acquiredFromBabaShop">Baba's Shop</li>
                <li v-if="acquiredFromZBattle">Z-Battle</li>
                <li v-if="acquiredFromWorldTournament">World Tournament</li>
              </ul>
            </BaseContent>
          </div>
        </BaseCard>

        <BaseCard
          v-if="
            (awakening_medal.stages !== null &&
              awakening_medal.stages.length > 0) ||
            awakening_medal.z_battle !== null
          "
          class="mx-auto xl:max-w-lg"
        >
          <template #heading>Stages</template>

          <div
            v-for="area in areas"
            v-if="
              awakening_medal.stages !== null &&
              awakening_medal.stages.length > 0
            "
            class="flex flex-col gap-2"
          >
            <img
              :alt="``"
              :src="`${page.props.app.cdn_url}/assets/en/${area.images['button']}`"
              class="w-fit"
            />

            <BaseContent
              v-for="quest in useGetQuestsFromStagesByArea(
                awakening_medal.stages,
                area,
              )"
              :key="quest.id"
            >
              <template #header>
                {{ quest.name }}
              </template>

              <div class="flex flex-row gap-2">
                <Link
                  v-for="stage in awakening_medal.stages.filter(
                    (s) => s.quest.id === quest.id,
                  )"
                  :href="
                    route('stages.show', {
                      stage: stage.id,
                    })
                  "
                >
                  {{ stage.difficulty }}
                </Link>
              </div>
            </BaseContent>
          </div>

          <img
            v-if="awakening_medal.z_battle"
            :alt="``"
            :src="`${page.props.app.cdn_url}/assets/en/${awakening_medal.z_battle.images['button']}`"
            class="w-fit"
          />
        </BaseCard>
      </div>

      <div>
        <BaseCard class="!flex-row flex-wrap justify-center !gap-0">
          <template #heading>Consumers</template>

          <DokkanCharacterList :characters="consumersSorted" />
        </BaseCard>
      </div>
    </div>
  </TheLayout>
</template>

<style scoped></style>
